import { useCallback, useEffect, useState } from "react";
import { Leaf, Zap, BookOpen, Siren, BadgeCheck, Clock3, MapPin, Bookmark, Award, FileBadge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CATEGORY_STYLES } from "@/lib/api";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import CheckinCard from "@/components/CheckinCard";
import CertificateModal from "@/components/CertificateModal";

const BADGE_ICONS = { leaf: Leaf, zap: Zap, "book-open": BookOpen, siren: Siren };

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [cert, setCert] = useState(null);

  const load = useCallback(() => api.get("/profile", { params: { user_id: user?.id || "demo-andini" } })
    .then((r) => setProfile(r.data)).catch(() => {}), [user?.id]);
  useEffect(() => { setProfile(null); load(); }, [load]);

  if (!profile) return <div className="py-32 text-center text-slate-400 font-semibold">Memuat profil...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <div className="bg-slate-900 rounded-2xl p-8 md:p-10 relative overflow-hidden">
        <div className="absolute -bottom-24 -right-16 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl" />
        <div className="flex flex-wrap items-center gap-6 relative">
          <div className="w-20 h-20 rounded-2xl bg-emerald-500 flex items-center justify-center font-heading font-black text-3xl text-white">
            {profile.name.charAt(0)}
          </div>
          <div>
            <h1 data-testid="profile-name" className="font-heading font-black text-3xl text-white tracking-tight">{profile.name}</h1>
            <p className="text-emerald-300 font-bold flex items-center gap-1.5 mt-1">
              <BadgeCheck className="w-4 h-4" /> {profile.action_profile} · <MapPin className="w-4 h-4" /> {profile.city}
            </p>
          </div>
          <div className="flex gap-8 sm:ml-auto">
            <div className="text-center">
              <p data-testid="total-hours" className="font-heading font-black text-3xl text-white">{profile.total_hours}</p>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-wide">Jam Terverifikasi</p>
            </div>
            <div className="text-center">
              <p data-testid="total-actions" className="font-heading font-black text-3xl text-white">{profile.total_actions}</p>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-wide">Aksi Selesai</p>
            </div>
          </div>
        </div>
      </div>

      <CheckinCard onVerified={(att) => {
        load();
        setCert({ name: att.volunteer_name, title: att.event_title, hours: att.hours, date: att.date, category: att.category });
      }} />

      <h2 className="font-heading font-black text-2xl text-slate-900 tracking-tight mt-12 mb-6 flex items-center gap-2">
        <Award className="w-6 h-6 text-emerald-600" /> Lencana Dampak
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {profile.badges.length === 0 ? (
          <p data-testid="no-badges-message" className="col-span-full text-slate-400 font-semibold text-sm bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center">
            Belum ada lencana. Ikuti dan selesaikan aksi pertamamu untuk mendapatkannya!
          </p>
        ) : profile.badges.map((b) => {
          const Icon = BADGE_ICONS[b.icon] || Award;
          return (
            <div key={b.name} data-testid={`badge-${b.name.toLowerCase().replace(/\s/g, "-")}`} className="card-lift bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <span className="inline-flex bg-emerald-50 text-emerald-600 rounded-2xl p-4 mb-3"><Icon className="w-7 h-7" /></span>
              <p className="font-heading font-extrabold text-slate-900">{b.name}</p>
              <p className="text-xs text-slate-500 mt-1">{b.desc}</p>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-12">
        <div className="lg:col-span-2">
          <h2 className="font-heading font-black text-2xl text-slate-900 tracking-tight mb-6 flex items-center gap-2">
            <Clock3 className="w-6 h-6 text-emerald-600" /> Riwayat Aksi
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {profile.history.length === 0 ? (
              <p data-testid="no-history-message" className="text-slate-400 font-semibold text-sm p-8 text-center">
                Belum ada riwayat aksi. Daftar aksi di Jelajahi Aksi lalu verifikasi kehadiranmu dengan PIN panitia.
              </p>
            ) : profile.history.map((h, i) => (
              <div key={i} data-testid={`history-row-${i}`} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-100 last:border-0">
                <div>
                  <p className="font-bold text-slate-900 flex items-center gap-1.5">
                    {h.title}
                    {h.verified && <BadgeCheck className="w-4 h-4 text-emerald-600" />}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{h.date}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${CATEGORY_STYLES[h.category] || "bg-slate-100"}`}>{h.category}</span>
                  <span className="font-heading font-black text-slate-900">{h.hours} jam</span>
                  {h.verified && (
                    <Button
                      data-testid={`cert-btn-${i}`}
                      size="sm"
                      variant="outline"
                      onClick={() => setCert({ name: h.volunteer_name || profile.name, title: h.title, hours: h.hours, date: h.date, category: h.category })}
                      className="rounded-full font-bold text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      aria-label={`Lihat sertifikat ${h.title}`}
                    >
                      <FileBadge className="w-4 h-4 mr-1" /> Sertifikat
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading font-black text-2xl text-slate-900 tracking-tight mb-6 flex items-center gap-2">
            <Bookmark className="w-6 h-6 text-emerald-600" /> Rencana Aksi Tersimpan
          </h2>
          {profile.saved_plans.length === 0 ? (
            <div data-testid="no-saved-plans" className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400">
              Belum ada rencana tersimpan. Coba AI Matchmaker dan simpan hasilnya di sini.
            </div>
          ) : (
            <div className="space-y-4">
              {profile.saved_plans.map((p) => (
                <div key={p.id} data-testid={`saved-plan-${p.id}`} className="bg-white rounded-2xl border border-slate-200 p-5">
                  <p className="font-heading font-extrabold text-emerald-700">{p.profile_title}</p>
                  <ul className="mt-2 space-y-1">
                    {p.event_titles.map((t) => <li key={t} className="text-sm text-slate-600">• {t}</li>)}
                  </ul>
                  <p className="text-xs text-slate-400 mt-2">{new Date(p.created_at).toLocaleDateString("id-ID")}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CertificateModal cert={cert} onClose={() => setCert(null)} />
    </div>
  );
}
